import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { getSessions, revokeSession } from './api';
import { formatWhen } from './format';
import { describeAuthError } from './passkeys';
import { useSignOut } from './useSignOut';

const METHOD: Record<string, string> = {
  passkey: 'passkey',
  authenticator: 'authenticator app',
  recovery: 'recovery code',
};

export function SessionsCard() {
  const client = useQueryClient();
  const signOut = useSignOut();
  const sessions = useQuery({ queryKey: ['auth-sessions'], queryFn: getSessions });
  const [confirmAll, setConfirmAll] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function revoke(id: string) {
    setMessage(null);
    try {
      await revokeSession(id);
      await client.invalidateQueries({ queryKey: ['auth-sessions'] });
    } catch (err) {
      setMessage(describeAuthError(err));
    }
  }

  return (
    <section className="card" aria-labelledby="sessions-title" data-testid="sessions">
      <h2 id="sessions-title" className="card-heading">
        Signed-in devices
      </h2>
      {sessions.isPending && <p role="status">Loading…</p>}
      {sessions.isError && (
        <p role="alert" className="error-text">
          Couldn't load devices.{' '}
          <button type="button" onClick={() => void sessions.refetch()}>
            Try again
          </button>
        </p>
      )}
      {sessions.data && (
        <ul className="rows">
          {sessions.data.map((s) => (
            <li key={s.id} data-current={s.current}>
              <div>
                <strong>{s.deviceLabel}</strong>
                {s.current && <span className="badge"> This device</span>}
                <p className="hint">
                  Signed in with {METHOD[s.method] ?? s.method} · last used{' '}
                  {formatWhen(s.lastSeenAt)}
                  {s.network ? ` · ${s.network}` : ''}
                </p>
              </div>
              {!s.current && (
                <button type="button" onClick={() => void revoke(s.id)}>
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!confirmAll ? (
        <button type="button" onClick={() => setConfirmAll(true)}>
          Sign out everywhere
        </button>
      ) : (
        <div className="stack" role="group" aria-label="Confirm sign out everywhere">
          <p className="card-detail">
            This signs out <strong>every</strong> device, including this one. You'll sign in again.
          </p>
          <div className="button-row">
            <button type="button" className="primary" onClick={() => void signOut(true)}>
              Yes, sign out everywhere
            </button>
            <button type="button" onClick={() => setConfirmAll(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {message && (
        <p role="alert" className="error-text">
          {message}
        </p>
      )}
    </section>
  );
}
